import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Star, Heart, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { MOOD_THEMES } from '../../constants/mediscribe';

const MoodPicker = ({
  currentMood,
  currentMoodIndex,
  setCurrentMoodIndex,
  favoriteMoods,
  setFavoriteMoods,
  setShowMoodInfo
}) => {
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const moodButtonRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);
  // Mar 3, 2026 — portal positioning for the popup. Fixed coords computed
  // from the trigger button's boundingClientRect so it appears attached to
  // the button but escapes the HeaderToolbar's `overflow-x-auto +
  // backdrop-blur + z-20` stacking context that was clipping/hiding it.
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const popupRef = useRef(null);

  const recomputePopupPos = () => {
    const btn = moodButtonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    // On desktop we align popup's left edge with the trigger's left, and
    // sit it 8px below the trigger. On mobile (< sm breakpoint) we let the
    // portal render as a bottom-sheet using the CSS classes below.
    setPopupPos({ top: r.bottom + 8, left: r.left });
  };

  const handleMoodMouseDown = () => {
    longPressFiredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setShowMoodPicker(true);
    }, 400);
  };

  const handleMoodMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  const handleMoodClick = () => {
    if (longPressFiredRef.current) return; // long press opened picker, don't double-toggle
    setShowMoodPicker(prev => !prev);
  };

  const selectMood = (index) => {
    setCurrentMoodIndex(index);
    setShowMoodPicker(false);
    const selectedMood = MOOD_THEMES[index];
    toast.success(`${selectedMood.emoji} ${selectedMood.name}`, { 
      description: selectedMood.description,
      duration: 2000 
    });
  };

  const toggleFavorite = (moodId, e) => {
    e.stopPropagation();
    setFavoriteMoods(prev => {
      if (prev.includes(moodId)) {
        return prev.filter(id => id !== moodId);
      } else if (prev.length < 3) {
        return [...prev, moodId];
      } else {
        toast.warning('Maximum 3 favorites allowed', { duration: 2000 });
        return prev;
      }
    });
  };

  const isFavorite = (moodId) => favoriteMoods.includes(moodId);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Mar 3, 2026 — popup is portaled to document.body so we also need
      // to exclude clicks INSIDE the popup itself (popupRef).
      const insideTrigger = moodButtonRef.current && moodButtonRef.current.contains(event.target);
      const insidePopup = popupRef.current && popupRef.current.contains(event.target);
      if (!insideTrigger && !insidePopup) {
        setShowMoodPicker(false);
      }
    };
    if (showMoodPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      // Keep the popup pinned to the trigger on scroll/resize.
      const reflow = () => recomputePopupPos();
      window.addEventListener('scroll', reflow, true);
      window.addEventListener('resize', reflow);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', reflow, true);
        window.removeEventListener('resize', reflow);
      };
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoodPicker]);

  // Compute initial position when the picker opens.
  useLayoutEffect(() => {
    if (showMoodPicker) recomputePopupPos();
  }, [showMoodPicker]);

  return (
    // Mar 3, 2026 — z-[9999] on the wrapper so the dropdown's own inner
    // z-index escapes the parent stacking context (was rendering BEHIND
    // the encounter tiles in the row below).
    <div className="relative flex items-center z-[9999]" ref={moodButtonRef}>
      <Button
        variant="ghost"
        onClick={handleMoodClick}
        onMouseDown={handleMoodMouseDown}
        onMouseUp={handleMoodMouseUp}
        onMouseLeave={handleMoodMouseUp}
        onTouchStart={handleMoodMouseDown}
        onTouchEnd={handleMoodMouseUp}
        className="h-8 sm:h-10 px-2 sm:px-3 rounded-l-lg rounded-r-none hover:bg-secondary flex items-center gap-1 sm:gap-2"
        data-testid="mood-toggle"
        title={`${currentMood.name} - Click to cycle favorites, hold for full list`}
      >
        <span className="text-lg sm:text-xl">{currentMood.emoji}</span>
        <span className="text-xs sm:text-sm font-medium text-foreground hidden sm:inline">{currentMood.name}</span>
      </Button>
      <Button
        variant="ghost"
        onClick={() => setShowMoodPicker(prev => !prev)}
        className="h-8 sm:h-10 w-6 px-0 rounded-r-lg rounded-l-none hover:bg-secondary border-l border-border/30 flex items-center justify-center"
        data-testid="mood-dropdown-caret"
        title="Open mood picker"
      >
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${showMoodPicker ? 'rotate-180' : ''}`} />
      </Button>
      
      {showMoodPicker && createPortal(
        // Mar 3, 2026 — Portal to document.body so we escape the
        // HeaderToolbar's `overflow-x-auto` + `relative z-20` +
        // `backdrop-blur-sm` stacking context. On desktop (sm+) we use
        // fixed positioning driven by the trigger's rect. On mobile we
        // pin to the bottom of the viewport as a bottom-sheet.
        <div
          ref={popupRef}
          className="mood-portal-popup fixed sm:top-auto sm:left-auto left-0 right-0 sm:right-auto bottom-0 sm:bottom-auto bg-card border border-border sm:rounded-lg rounded-t-xl shadow-2xl p-3 sm:p-2 sm:min-w-[280px] max-h-[80vh] overflow-y-auto"
          style={{
            // Only apply computed top/left on desktop viewports.
            // Tailwind's `sm:` breakpoint = 640px.
            ...(window.innerWidth >= 640
              ? { top: popupPos.top, left: popupPos.left, right: 'auto', bottom: 'auto' }
              : {}),
            zIndex: 100000,
          }}
        >
          <div className="sm:hidden flex justify-center pb-2 mb-2 border-b border-border">
            <div className="w-12 h-1 bg-muted-foreground/30 rounded-full"></div>
          </div>
          
          {favoriteMoods.length > 0 && (
            <>
              <div className="text-xs text-muted-foreground px-2 pb-2 flex items-center gap-1">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> Favorites
              </div>
              <div className="grid grid-cols-3 gap-1 mb-2 pb-2 border-b border-border">
                {favoriteMoods.map(moodId => {
                  const mood = MOOD_THEMES.find(m => m.id === moodId);
                  const moodIndex = MOOD_THEMES.findIndex(m => m.id === moodId);
                  if (!mood) return null;
                  return (
                    <button
                      key={mood.id}
                      onClick={() => selectMood(moodIndex)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-md transition-colors ${
                        moodIndex === currentMoodIndex 
                          ? 'bg-primary/20 ring-2 ring-primary' 
                          : 'hover:bg-secondary'
                      }`}
                      title={mood.name}
                    >
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                        style={{ backgroundColor: mood.color }}
                      >
                        {mood.emoji}
                      </div>
                      <span className="text-xs text-foreground truncate w-full text-center">{mood.name}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          
          <div className="text-xs text-muted-foreground px-2 pb-2">All Moods (tap star to favorite)</div>
          <div className="grid grid-cols-1 gap-1 max-h-[300px] overflow-y-auto">
            {MOOD_THEMES.map((mood, index) => (
              <button
                key={mood.id}
                onClick={() => selectMood(index)}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-left transition-colors ${
                  index === currentMoodIndex 
                    ? 'bg-primary/20 text-primary ring-2 ring-primary' 
                    : 'hover:bg-secondary text-foreground'
                }`}
                data-testid={`mood-option-${mood.id}`}
              >
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                  style={{ backgroundColor: mood.color }}
                >
                  {mood.emoji}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{mood.name}</div>
                  <div className="text-xs text-muted-foreground">{mood.description}</div>
                </div>
                <button
                  onClick={(e) => toggleFavorite(mood.id, e)}
                  className="p-1 rounded hover:bg-primary/10 transition-colors"
                  title={isFavorite(mood.id) ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <Star 
                    className={`w-4 h-4 ${isFavorite(mood.id) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`}
                  />
                </button>
              </button>
            ))}
          </div>
          
          <div className="pt-2 mt-2 border-t border-border">
            <button
              onClick={() => { setShowMoodPicker(false); setShowMoodInfo(true); }}
              className="w-full text-xs text-muted-foreground hover:text-primary flex items-center justify-center gap-1 py-1"
            >
              <Heart className="w-3 h-3" />
              Why mood matters
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MoodPicker;
